import React, { useEffect, useState } from "react";
import axios from "axios";
import { IoSaveOutline } from "react-icons/io5";
import { BASE_URL } from "../../Constants";
import ProgressLoader from "../ProgressLoader/ProgressLoader";

// Matches the DEPARTMENTS enum on the backend User model
const departments = [
  "HR",
  "Operations",
  "Engineering",
  "Project management",
  "QA",
  "Admin",
  "Marketing",
  "Devops",
];

// Mirrors the backend's getWeekNumber() in the timesheet router,
// so the week number computed here always matches what the API expects.
const getWeekNumber = (date) => {
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
  const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
};

const formatShortDate = (date) =>
  date.toLocaleDateString("en-US", { month: "short", day: "numeric" });

// Builds { weekNumber: { start, end } } for every day in the given year,
// using the exact same getWeekNumber() logic the backend uses — so the
// dropdown's week numbers and date ranges always match what the API expects.
const buildWeeksForYear = (year) => {
  const weeks = {};
  const cursor = new Date(year, 0, 1);
  while (cursor.getFullYear() === year) {
    const wn = getWeekNumber(cursor);
    if (!weeks[wn]) {
      weeks[wn] = { start: new Date(cursor), end: new Date(cursor) };
    } else {
      weeks[wn].end = new Date(cursor);
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return weeks;
};

const today = new Date();
const CURRENT_WEEK_NUMBER = getWeekNumber(today);
const CURRENT_YEAR = today.getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];

const AllocationPage = () => {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [weekNumber, setWeekNumber] = useState(CURRENT_WEEK_NUMBER);

  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingUserId, setSavingUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");

  // Local edits, keyed by userId, before they're saved
  const [editedValues, setEditedValues] = useState({});

  const weeksForYear = React.useMemo(() => buildWeeksForYear(year), [year]);
  const weekOptions = Object.keys(weeksForYear)
    .map(Number)
    .sort((a, b) => a - b);

  const isCurrentWeekSelected =
    year === CURRENT_YEAR && weekNumber === CURRENT_WEEK_NUMBER;

  const selectedWeekRange = weeksForYear[weekNumber];

  const fetchAllocations = async () => {
    if (!weekNumber || !year) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/api/allocation/getMaxAllocation/${weekNumber}/${year}`
      );
      setAllocations(response.data?.data || []);
      setEditedValues({});
    } catch (error) {
      console.error(
        "Error fetching allocations:",
        error.response ? error.response.data : error.message
      );
      alert("Failed to fetch allocation data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekNumber, year]);

  const handleFieldChange = (userId, field, value) => {
    setEditedValues((prev) => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [field]: value,
      },
    }));
  };

  const getRowValue = (row, field) => {
    if (editedValues[row.userId]?.[field] !== undefined) {
      return editedValues[row.userId][field];
    }
    return row[field];
  };

  const handleSaveRow = async (row) => {
    const edited = editedValues[row.userId] || {};
    const maxAllocationValue =
      edited.maxAllocation !== undefined
        ? edited.maxAllocation
        : row.maxAllocation;
    const departmentValue =
      edited.department !== undefined ? edited.department : row.department;

    if (maxAllocationValue === "" || maxAllocationValue === null) {
      alert("Max allocation is required.");
      return;
    }

    setSavingUserId(row.userId);
    try {
      // Save max allocation for this user/week/year
      await axios.post(`${BASE_URL}/api/allocation/setMaxAllocation`, {
        userId: row.userId,
        weekNumber,
        year,
        maxAllocation: Number(maxAllocationValue),
      });

      // Save department only if it actually changed
      if (departmentValue !== row.department) {
        await axios.put(
          `${BASE_URL}/api/user/updateDepartment/${row.userId}`,
          { department: departmentValue || null }
        );
      }

      // Reflect the saved values locally without a full refetch
      setAllocations((prev) =>
        prev.map((item) =>
          item.userId === row.userId
            ? {
                ...item,
                maxAllocation: Number(maxAllocationValue),
                department: departmentValue,
              }
            : item
        )
      );
      setEditedValues((prev) => {
        const next = { ...prev };
        delete next[row.userId];
        return next;
      });
    } catch (error) {
      console.error(
        "Error saving allocation:",
        error.response ? error.response.data : error.message
      );
      alert("Failed to save allocation for this user.");
    } finally {
      setSavingUserId(null);
    }
  };

  const filteredAllocations = allocations.filter((row) => {
    const matchesSearch = row.name
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesDepartment =
      departmentFilter === "all" || row.department === departmentFilter;
    return matchesSearch && matchesDepartment;
  });

  return (
    <div className="max-w-7xl mx-auto overflow-x-auto">
      <h1 className="text-2xl font-semibold mb-6 font-montserrat">
        Allocation
      </h1>
      {loading && <ProgressLoader />}

      {/* Filters */}
      <div className="mb-4 flex flex-col md:flex-row gap-4 md:items-end">
        <div>
          <label className="block text-sm font-semibold mb-1 font-montserrat">
            Year
          </label>
          <select
            value={year}
            onChange={(e) => {
              const newYear = Number(e.target.value);
              setYear(newYear);
              // Reset to current week if switching back to the current year,
              // otherwise default to week 1 of the newly selected year.
              setWeekNumber(
                newYear === CURRENT_YEAR ? CURRENT_WEEK_NUMBER : 1
              );
            }}
            className="p-2 border border-gray-300 rounded"
          >
            {YEAR_OPTIONS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1 font-montserrat">
            Week
          </label>
          <select
            value={weekNumber || ""}
            onChange={(e) => setWeekNumber(Number(e.target.value))}
            className={`p-2 border rounded font-montserrat ${
              isCurrentWeekSelected
                ? "border-blue-500 bg-blue-50 text-blue-700 font-semibold ring-1 ring-blue-400"
                : "border-gray-300"
            }`}
          >
            {weekOptions.map((wn) => {
              const range = weeksForYear[wn];
              const isCurrent = year === CURRENT_YEAR && wn === CURRENT_WEEK_NUMBER;
              return (
                <option key={wn} value={wn}>
                  Week {wn} ({formatShortDate(range.start)} -{" "}
                  {formatShortDate(range.end)})
                  {isCurrent ? " • Current" : ""}
                </option>
              );
            })}
          </select>
        </div>

        {isCurrentWeekSelected && (
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold font-montserrat self-end mb-1">
            Current Week
          </span>
        )}

        <div className="text-sm text-gray-600 font-opensans self-end mb-1">
          {selectedWeekRange && (
            <span>
              {formatShortDate(selectedWeekRange.start)} –{" "}
              {formatShortDate(selectedWeekRange.end)}, {year}
            </span>
          )}
        </div>

        <input
          type="text"
          placeholder="Search by name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="p-2 border border-gray-300 rounded md:w-64 font-montserrat placeholder:text-xs md:placeholder:text-base"
        />

        <select
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          className="p-2 border border-gray-300 rounded"
        >
          <option value="all">All Departments</option>
          {departments.map((dept) => (
            <option key={dept} value={dept}>
              {dept}
            </option>
          ))}
        </select>
      </div>

      {filteredAllocations.length === 0 && !loading && (
        <p>No users found for this week.</p>
      )}

      <table className="table-auto w-full border-collapse border border-gray-300 shadow-lg text-sm">
        <thead className="font-montserrat">
          <tr className="bg-gray-100 text-left">
            <th className="border p-4">Name</th>
            <th className="border p-4">Email</th>
            <th className="border p-4">Employment Status</th>
            <th className="border p-4">Department</th>
            <th className="border p-4">Max Allocation</th>
            <th className="border p-4">Actions</th>
          </tr>
        </thead>
        <tbody className="text-xs font-opensans">
          {filteredAllocations.map((row, index) => {
            const hasChanges = !!editedValues[row.userId];
            return (
              <tr
                key={row.userId}
                className={`${
                  index % 2 === 0 ? "bg-white" : "bg-gray-50"
                } hover:bg-gray-100`}
              >
                <td className="border p-4">{row.name}</td>
                <td className="border p-4">{row.email}</td>
                <td className="border p-4 capitalize">
                  {row.employmentStatus}
                </td>
                <td className="border p-4">
                  <select
                    className="p-1 border border-gray-300 rounded"
                    value={getRowValue(row, "department") || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        row.userId,
                        "department",
                        e.target.value
                      )
                    }
                  >
                    <option value="">Not set</option>
                    {departments.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="border p-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      className="p-1 border border-gray-300 rounded w-24"
                      value={
                        getRowValue(row, "maxAllocation") === null
                          ? ""
                          : getRowValue(row, "maxAllocation")
                      }
                      onChange={(e) =>
                        handleFieldChange(
                          row.userId,
                          "maxAllocation",
                          e.target.value
                        )
                      }
                    />
                    {row.isAutoCopied &&
                      editedValues[row.userId]?.maxAllocation === undefined && (
                        <span
                          title="System-defaulted — carried forward or Fulltime default, not yet manually confirmed"
                          className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold"
                        >
                          Auto
                        </span>
                      )}
                  </div>
                </td>
                <td className="border p-4">
                  <button
                    onClick={() => handleSaveRow(row)}
                    disabled={!hasChanges || savingUserId === row.userId}
                    className={`flex items-center gap-1 ${
                      hasChanges
                        ? "text-blue-600 hover:text-blue-800"
                        : "text-gray-300 cursor-not-allowed"
                    }`}
                  >
                    <IoSaveOutline className="text-xl" />
                    {savingUserId === row.userId ? "Saving..." : "Save"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AllocationPage;